import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Content, Episode } from '../../apiService';
import { useUser } from '../../CacheProvider';
import { __t } from '../../i18n';
import { IconLock } from '../../icons';
import { Button, Flex, Row, Text } from '../../styles';
import { getEpisodeIdentifier } from '../../utils';
import SeasonSelector from './SeasonSelector';
import { flattenEpisodes, getSeasons, isLocked } from './shortEpisodes';

type EpisodeItemsProps = {
  content: Content;
  activeFileId?: string;
  /** Nothing in the short is playable, so the grid is shown but none of it can be chosen. */
  disabled?: boolean;
  onSelect: (fileId?: string) => void;
};

export default function EpisodeItems({ content, activeFileId, disabled, onSelect }: EpisodeItemsProps) {
  const { authorized, hasSubscription } = useUser();
  const hasFullAccess = authorized && hasSubscription;

  const seasons = useMemo(() => getSeasons(content), [content]);
  // Follow the season the active episode belongs to, so swiping past a season boundary moves
  // the grid with it.
  const activeSeasonIndex = useMemo(
    () => flattenEpisodes(content).find(({ file_id }) => file_id === activeFileId)?.seasonIndex ?? 0,
    [content, activeFileId],
  );

  const [seasonIndex, setSeasonIndex] = useState(activeSeasonIndex);

  useEffect(() => {
    setSeasonIndex(activeSeasonIndex);
  }, [activeSeasonIndex, seasons]);

  const episodes = seasons[seasonIndex]?.episodes || [];
  if (!episodes.length) return null;

  return (
    <Section>
      <Flex justify="space-between" align="center" my={3}>
        <Text variant="h2" weight="medium">
          {__t('content.type.episodes')}
        </Text>
        <SeasonSelector seasons={seasons} active={seasonIndex} onChange={setSeasonIndex} />
      </Flex>

      <EpisodeScroller>
        {/* Five across only once the panel is at its full width; below lg it is narrower and
            five columns leave the labels too cramped to read. */}
        <Row cols={{ xs: 4, md: 4, lg: 5 }} gap={1}>
          {episodes.map((episode, idx) => (
            <EpisodeButton
              key={episode.file_id ?? episode.id ?? idx}
              episode={episode}
              label={episodeLabel(episode, idx)}
              locked={isLocked(episode, hasFullAccess)}
              disabled={disabled}
              active={!!episode.file_id && episode.file_id === activeFileId}
              onSelect={onSelect}
            />
          ))}
        </Row>
      </EpisodeScroller>
    </Section>
  );
}

// Titled episodes are named by their title; the rest fall back to being numbered.
const episodeLabel = (episode: Episode, idx: number) => (
  episode.episode_title || getEpisodeIdentifier({ episode: episode.episode_number ?? episode.episode ?? idx + 1 })
);

type EpisodeButtonProps = {
  episode: Episode;
  label: string;
  locked: boolean;
  disabled?: boolean;
  active: boolean;
  onSelect: (fileId?: string) => void;
};

// `locked` is the paywall, and wears its badge; `disabled` is the whole short being off limits,
// which the notice over the player already explains.
const EpisodeButton = ({ episode, label, locked, disabled, active, onSelect }: EpisodeButtonProps) => (
  <ItemButton
    type="button"
    variant={active ? 'success' : 'secondary'}
    fullWidth
    disabled={locked || disabled}
    onClick={() => onSelect(episode.file_id)}
  >
    <EpisodeLabel>{label}</EpisodeLabel>
    {locked && (
      <LockBadge>
        <IconLock />
      </LockBadge>
    )}
  </ItemButton>
);

// On desktop this claims whatever height the panel has left, so the grid runs to the panel's
// bottom padding instead of stopping short. In the mobile sheet it stays unconstrained and the
// sheet itself does the scrolling.
const Section = styled.div(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

const EpisodeScroller = styled.div(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minHeight: 0,
    overflowY: 'visible',
    // Without this, overflow-x computes to `auto` and Row's negative inline-start margin
    // produces a stray horizontal scrollbar.
    overflowX: 'visible',
    WebkitOverflowScrolling: 'touch',
    // Keeps the bar off the buttons — in RTL the vertical scrollbar sits on the inline-end side.
    [theme.logicalProp('padding', 'end')]: theme.spacing(1),
    // A hairline bar. Styling ::-webkit-scrollbar at all also drops the stepper arrows.
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.background[300][0]} transparent`,
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.background[300][0],
      borderRadius: theme.radius.pill,
    },
  },
}));

const ItemButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  overflow: 'visible',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: theme.spacing(5),
  padding: theme.spacing(0, 1),
  whiteSpace: 'normal',
  ...theme.font.responsiveSize(0),
}));

const EpisodeLabel = styled.span({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  lineHeight: 1.3,
  textAlign: 'center',
});

const LockBadge = styled.span(({ theme }) => {
  const [bg, fg] = theme.palette.warning[400];
  return {
    zIndex: 2,
    position: 'absolute',
    bottom: '-.35em',
    insetInlineStart: '-.35em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5em',
    height: '1.5em',
    borderRadius: '50%',
    backgroundColor: bg,
    color: fg,
    svg: {
      width: '.7em',
      height: '.7em',
    },
  };
});
